import React, {useState, useEffect, lazy} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import ReactCountryFlag from 'react-country-flag'
import _ from 'lodash'
import moment from 'moment'
import {useFormik} from 'formik'
import {Box, Button, CircularProgress, Typography} from '@mui/material'

import {type RootState, type AppDispatch} from 'redux/store'
import FormikAutocomplete from 'shared/FormikStationAutoComplete'
import FormikSelect from 'shared/FormikSelect'
import {viewScheduleFormValidations} from 'features/HomePage/ViewSchedules/viewSchedules.utils'
import {MAX_DATE_BY_CARRIER} from 'features/HomePage/ViewSchedules/viewSchedules.constant'
import {fetchCarrierList} from 'redux/common.slice'
import {PAGE_NAME} from 'config/constant'
import {fetchSchedules} from 'features/HomePage/ViewSchedules/viewSchedules.slice'
import 'features/HomePage/ViewSchedules/index.scss'
import {toggleLoginPopup, navigatePath, viewScheduleValues} from 'features/Login/login.slice'

const FormikDatePicker = lazy(async () => await import('shared/FormikDatePicker'))
const FormikDateTimePicker = lazy(async () => await import('shared/FormikDateTimePicker'))

interface FormProps {
    dateMode: 'date' | 'datetime'
    navigateURL?: string
    autoFocus?: boolean
}

const Form = ({dateMode, navigateURL, autoFocus}: FormProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const [isDisabled, setDisabled] = useState(true)
    const [maxDays, setMaxDays] = useState(30)
    const [carrierOptions, setCarrierOptions] = useState([])
    const defaultValues = useSelector((state: any) => state.viewSchedules.initialValues)
    const securityParams = useSelector((state: RootState) => state.login.loginDetails?.securityParams)
    const isLoggedIn = useSelector((state: any) => state?.login?.isLoggedIn)
    const state = useSelector((state: any) => state.viewSchedules)
    const carrierList = useSelector((state: any) => state.common.carrier)
    let initialValues = {
        flightDateTime: new Date(),
        carrier: '-Select-',
        from: '',
        to: ''
    }
    if (dateMode !== 'date') {
        initialValues = {...defaultValues}
    }
    // user id need to send to API
    const formik = useFormik({
        initialValues,
        validationSchema: viewScheduleFormValidations,
        onSubmit: (values: any) => {
            if (isLoggedIn) {
                dispatch(fetchSchedules({...values, userId: _.get(securityParams, 'userID', 'CPSGUEST')}))
                    .then((resp) => {
                        navigate(navigateURL ?? '')
                    })
                    .catch(() => {})
            } else {
                dispatch(toggleLoginPopup(true))
                dispatch(navigatePath(navigateURL))
                dispatch(viewScheduleValues(values))
            }
        }
    })

    useEffect(() => {
        if (
            formik?.values?.carrier !== '-Select-' &&
            formik?.values?.flightDateTime !== null &&
            formik?.values?.from !== '' &&
            formik?.values?.to !== '' &&
            formik.isValid
        ) {
            setDisabled(false)
        } else {
            setDisabled(true)
        }
    }, [formik])

    useEffect(() => {
        const sortedList = _.cloneDeep(carrierList).sort((a, b) => a.displayName.localeCompare(b.displayName))
        const list = sortedList?.map((temp) => ({
            value: `${temp.code} - ${temp.displayName} (${temp.name})`,
            label: `${temp.displayName} (${temp.name})`
        }))
        setCarrierOptions(list)
    }, [carrierList])

    useEffect(() => {
        const matches = formik.values.carrier?.match(/\((.*?)\)/)
        let carrier = ''
        if (matches && matches.length >= 2) {
            carrier = matches[1]
        }
        const maxDays = MAX_DATE_BY_CARRIER[carrier] || 30
        setMaxDays(maxDays)
        const date = formik.values.flightDateTime
        if (date.getTime() > moment().add(parseInt(maxDays), 'days').toDate().getTime()) {
            void formik.setFieldValue('flightDateTime', new Date())
        }
    }, [formik.values.carrier])

    useEffect(() => {
        void dispatch(
            fetchCarrierList({
                pageName: PAGE_NAME[31]
            })
        )
    }, [])
    const menuItemStyle = {
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        '&:hover': {
            color: '#356eab',
            backgroundColor: '#f1f7fd'
        }
    }

    return (
        <form className='form-container' onSubmit={formik.handleSubmit}>
            <Box className='form-input-container'>
                <label htmlFor='carrier'>
                    Carrier<span>&#42;</span>
                </label>
                <FormikSelect
                    id='carrier'
                    formik={formik}
                    placeholder='- Select Carrier -'
                    options={carrierOptions}
                    className='formik-select'
                    autoFocus={autoFocus}
                    MenuClassName='viewSchedules-dropdown-list'
                    disabled={state.isLoading}
                />
            </Box>
            <Box className='form-input-container'>
                {dateMode === 'date' ? (
                    <>
                        <label htmlFor='flightDateTime'>
                            Flight Date<span>&#42;</span>
                        </label>
                        <FormikDatePicker
                            id='flightDateTime'
                            formik={formik}
                            className='formik-date'
                            maxDays={maxDays}
                            disabled={state.isLoading}
                        />
                    </>
                ) : (
                    <>
                        <label htmlFor='flightDateTime'>
                            Flight Date/Time<span>&#42;</span>
                        </label>
                        <FormikDateTimePicker
                            id='flightDateTime'
                            formik={formik}
                            className='formik-date'
                            disabled={state.isLoading}
                            maxDays={maxDays}
                        />
                    </>
                )}
            </Box>
            <Box className='form-input-container'>
                <label htmlFor='from'>
                    From<span>&#42;</span>
                </label>
                <FormikAutocomplete
                    id='from'
                    formik={formik}
                    disabled={state.isLoading}
                    className='formik-autocomplete'
                    renderOption={(p: any, o: any) => (
                        <Box
                            component='li'
                            {...p}
                            key={o.code}
                            sx={menuItemStyle}
                            className='formik_autocomplete__items'
                        >
                            <Typography variant='h6'>{`${o.code}: ${o.city}`}</Typography>
                            <Typography variant='h6'>{o.country}&nbsp;</Typography>
                            <Box className='img-container'>
                                <ReactCountryFlag countryCode={o.countryCode} svg />
                            </Box>
                        </Box>
                    )}
                    message='Type airport code to view results.'
                />
            </Box>
            <Box className='form-input-container'>
                <label htmlFor='to'>
                    To<span>&#42;</span>
                </label>
                <FormikAutocomplete
                    id='to'
                    formik={formik}
                    disabled={state.isLoading}
                    className='formik-autocomplete'
                    renderOption={(p: any, o: any) => (
                        <Box
                            component='li'
                            {...p}
                            key={o.code}
                            sx={menuItemStyle}
                            className='formik_autocomplete__items'
                        >
                            <Typography variant='h6'>{`${o.code}: ${o.city}`}</Typography>
                            <Typography variant='h6'>{o.country}&nbsp;</Typography>
                            <Box className='img-container'>
                                <ReactCountryFlag countryCode={o.countryCode} svg />
                            </Box>
                        </Box>
                    )}
                    message='Type airport code to view results.'
                />
            </Box>
            <Button type='submit' variant='contained' className='btn-primary' disabled={isDisabled || state.isLoading}>
                {state.isLoading ? (
                    <CircularProgress size={24} className='view-schedules__submit-btn-progress' />
                ) : (
                    'View schedules'
                )}
            </Button>
        </form>
    )
}

Form.defaultProps = {
    dateMode: 'date',
    navigateURL: '',
    autoFocus: false
}

export default Form
